import { supabase } from '@/lib/supabase';

/**
 * DB-backed catalog (0078).
 *
 * The /pricing and /billing pages read from the subscription_plans table
 * via getCatalog(). Admins edit via /admin/pricing.
 *
 * Falls back to nothing if the table is unreachable — calling pages should
 * handle empty arrays.
 */

export interface SubscriptionPlan {
  id: string;
  tier: 'standard' | 'pro' | 'enterprise';
  name: string;
  blurb: string;
  monthly_cad: number | null;
  annual_cad: number | null;
  cta: 'start_trial' | 'contact_sales';
  limits_blurb: string | null;
  is_published: boolean;
  sort_order: number;
  stripe_price_id_monthly: string | null;
  stripe_price_id_annual: string | null;
  created_at: string;
  updated_at: string;
}

export async function getCatalog(): Promise<SubscriptionPlan[]> {
  const { data, error } = await supabase
    .from('subscription_plans')
    .select('*')
    .eq('is_published', true)
    .order('sort_order');
  if (error) throw error;
  return (data ?? []) as SubscriptionPlan[];
}

export async function adminListAllPlans(): Promise<SubscriptionPlan[]> {
  const { data, error } = await supabase.rpc('admin_list_subscription_plans');
  if (error) throw error;
  return (data ?? []) as SubscriptionPlan[];
}

export async function adminUpdatePlan(input: {
  tier: 'standard' | 'pro' | 'enterprise';
  name: string;
  blurb: string;
  monthlyCAD: number | null;
  annualCAD: number | null;
  cta: 'start_trial' | 'contact_sales';
  limitsBlurb: string | null;
  isPublished: boolean;
}): Promise<SubscriptionPlan> {
  const { data, error } = await supabase.rpc('admin_update_subscription_plan', {
    p_tier: input.tier,
    p_name: input.name,
    p_blurb: input.blurb,
    p_monthly_cad: input.monthlyCAD,
    p_annual_cad: input.annualCAD,
    p_cta: input.cta,
    p_limits_blurb: input.limitsBlurb,
    p_is_published: input.isPublished,
  });
  if (error) throw error;
  return data as SubscriptionPlan;
}

export function findPlan(plans: SubscriptionPlan[], tier: SubscriptionPlan['tier']): SubscriptionPlan | undefined {
  return plans.find((p) => p.tier === tier);
}

export function planPrice(plan: SubscriptionPlan, period: 'monthly' | 'annual'): number | null {
  return period === 'annual' ? plan.annual_cad : plan.monthly_cad;
}

/**
 * Annual savings as percent vs monthly. Computed per-tier from the plan,
 * defaulting to 17 if either price is null.
 */
export function annualSavingsPercent(plan: SubscriptionPlan): number {
  if (plan.monthly_cad == null || plan.annual_cad == null) return 17;
  if (plan.monthly_cad === 0) return 17;
  return Math.round(((plan.monthly_cad - plan.annual_cad) / plan.monthly_cad) * 100);
}
